import { useMemo, useState } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { Auswahlfeld } from './Auswahlfeld'
import { ohneStatistikkonten } from './Festkonten'
import { Nachfrage } from './Nachfrage'
import { Prozessfenster } from './Prozessfenster'
import { useDarf } from './Rechte'
import { Schrittfolge } from './Schrittfolge'
import { beschriftung, katalogbezeichnung, satz, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { Verweissatz } from './Verweissatz'
import { adresseVon } from './Abbruchverweise'
import type {
  Schrittbefund,
  Schrittzeile,
  Stammdatenkatalog,
} from './typen'

/**
 * Die Schritte des Konzernabschlusses (2.9 Abschnitt 10, R782, R783).
 *
 * **Der Befund vom 2026-09-26:** Das Modell *Art → Prozess → Vorgang* stand
 * seit dem 2026-09-19 im Fachkonzept; in der Datenbank stand davon nur der
 * Vorgang. Dieser Bildschirm zeigte deshalb nur, was gerechnet wurde — und war
 * ohne Lauf leer.
 *
 * **Auftrag des Auftraggebers:** *„Die Schritte sind frei durch den Anwender
 * definierbar. Wir sollten nur die Konsolidierungswerkzeuge bereitstellen, aus
 * denen sich der Anwender die Abschlussprozesse zusammenstellen darf."*
 *
 * **Eine Liste, die des Programms** (R837, Entscheidung vom 2026-09-29). Bis
 * dahin standen hier zwei Listen untereinander — oben eine Folge mit Pfeilen,
 * die nichts bewirkten, darunter die gerechneten Vorgänge in anderer
 * Reihenfolge — und eine Warnung, die das Gegenteil dessen behauptete, was
 * das Programm tut. Jetzt stehen die Schritte in der Reihenfolge, in der
 * gerechnet wird, und neben jedem die Zahl seiner Buchungen aus dem letzten
 * Lauf. Jede Maßnahme liest den unveränderten Einzelabschluß und bucht in die
 * Konsolidierungsspalte, nie zurück (R781).
 *
 * **Gewarnt wird, nicht blockiert** (R783): *„Eine Warnmeldung kann dem
 * Anwender helfen, doppelt belegte Positionen oder auch fehlende Positionen zu
 * erkennen."*
 */
/**
 * Die Befundarten, die den Konzernabschluss verhindern (R802).
 *
 * **Ein fehlendes Werkzeug ist eine Warnung, ein fehlendes Differenzenkonto
 * nicht.** Wer den Schritt weglässt, bekommt einen Abschluss, in dem etwas
 * stehen bleibt; wer das Konto weglässt, bekommt gar keinen.
 */
const BLOCKIERT = new Set(['ohne-differenzkonto', 'ohne-differenzkonto-waehrung'])

/*
  **Ohne Lauf und ohne Zahl der Buchungen** (7.2 Abschnitt 36, R1142, R1143 —
  Entscheidung vom 2026-10-06): Bis dahin trug die Tabelle die Spalte
  „Buchungen im Lauf 57" mit der Zahl je Schritt (R837). Die Laufnummer
  beantwortet keine Frage, die Zahl ist eine Menge ohne Entscheidungswert; was
  ein Schritt gebucht hat, zeigt sein Fenster.
*/
export function Konsolidierungsschritte() {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.

    **Neue Kennung seit dem 2026-09-29** (R837): Die Tabelle hat die Spalte
    *Werkzeug* verloren und *Buchungen* bekommen. Gezogene Breiten der alten
    Tabelle lägen sonst auf den falschen Spalten.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('konsolidierungsschritte:folge')
  // Was der Angemeldete nicht darf, ist nicht da (8.1 Abschnitt 7, R1180).
  const darf = useDarf()

  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [anlegen, setAnlegen] = useState(false)
  const [entwurf, setEntwurf] = useState<{ werkzeug: string; name: string }>({ werkzeug: '', name: '' })
  const [loeschen, setLoeschen] = useState<Schrittzeile | null>(null)
  /*
    **Nur die Vorlage ab Werk** (2.13 Abschnitt 20, R1610): Bis zum
    2026-10-13 ließ sich hier auch die Folge eines Konzernabschlusses wählen
    und pflegen — dieselben Schritte an zwei Stellen. Die Schritte eines
    bestehenden Abschlusses pflegt man an ihm, unter Konzernabschlüsse.
  */
  /**
   * Der Prozeß, dessen Fenster offensteht — einer zur Zeit (R610, R825).
   *
   * **Dasselbe Fenster wie unter dem Konzernabschluß**: Bis zum 2026-09-27
   * hatte dieser Bereich eigene Formulare unter der Zeile, und was dort
   * gebaut wurde, fehlte drüben.
   */
  const [fenster, setFenster] = useState<Schrittzeile | null>(null)

  // Die Folge ist der Bestand der Seite — ein Hauptabruf (R1211): Kam sie
  // nicht, steht „Ließ sich nicht laden" da und nicht eine leere Seite.
  const bildabruf = useAbruf(() => api.konsolidierungsschritte(null), [])
  const bild = bildabruf.wert

  // Namen: Nachschlagen im Hintergrund (7.2 Abschnitt 30, R1007, R1211).
  const katalog = useHintergrundabruf<Stammdatenkatalog | null>(() => api.stammdatenkatalog(), [], null)

  const positionswahl = useMemo(
    () =>
      (katalog?.positionen ?? []).map((p) => ({
        wert: p.schluessel,
        text: katalogbezeichnung(p),
        stillgelegtAb: p.stillgelegtAb,
      })),
    [katalog],
  )

  // Ohne Statistikkonten: Die Konsolidierung bebucht sie nie (R1734).
  const kontowahl = useMemo(
    () =>
      ohneStatistikkonten(katalog?.konten ?? [], (k) => k.schluessel).map((k) => ({
        wert: k.schluessel,
        text: katalogbezeichnung(k),
        stillgelegtAb: k.stillgelegtAb,
      })),
    [katalog],
  )

  const werkzeugwahl = useMemo(
    () =>
      (bild?.werkzeuge ?? []).map((w) => ({
        wert: w.schluessel,
        text: t(`werkzeug.${w.schluessel}` as Sprachschluessel),
      })),
    [bild],
  )

  /**
   * **Wohin eine Meldung der Folge führt** (7.1 Abschnitt 13, R1528) — an
   * Ort und Stelle, mit der Handlung vorbereitet. `null`, wo der Angemeldete
   * die Handlung nicht darf: Dann steht der Satz ohne Verweis da.
   */
  function verweisDesBefunds(b: Schrittbefund): (() => void) | null {
    const name = b.meldung.werte.find((w) => w.feld === 'schritt')?.wert
    const passend = (bild?.folge ?? []).filter((s) => s.werkzeug === b.werkzeug)
    const schritt = passend.find((s) => beschriftung(s.namen) === name) ?? passend[0]

    switch (b.art) {
      case 'fehlt':
        return darf('konsolidierungsschrittPflegen')
          ? () => {
              setAnlegen(true)
              setEntwurf({ werkzeug: b.werkzeug, name: t(`werkzeug.${b.werkzeug}` as Sprachschluessel) })
            }
          : null
      case 'doppelt':
      case 'ohne-differenzkonto':
      case 'ohne-differenzkonto-waehrung':
        return schritt ? () => setFenster(schritt) : null
      case 'unbekannt':
        return schritt && darf('konsolidierungsschrittEntfernen') ? () => setLoeschen(schritt) : null
      default:
        return null
    }
  }

  async function tue(was: () => Promise<Schrittzeile[]>, hinweis?: string) {
    setLaeuft(true)
    setMeldung(null)

    try {
      const folge = await was()
      // Die Befunde hängen an der Folge und werden vom Server gerechnet — ein
      // erneuter Ruf holt sie mit, statt sie hier ein zweites Mal abzuleiten.
      //
      // **Und zwar die Folge, die dasteht** (2026-09-26): Der Ruf ging ohne
      // Konzernabschluß hinaus und holte damit die Vorlage ab Werk. Wer einen
      // Abschluß gewählt hatte, sah nach jeder Pflege die falsche Folge —
      // seine Änderung war gespeichert und verschwand vom Bildschirm.
      bildabruf.setze(await api.konsolidierungsschritte(null))
      setLoeschen(null)
      setAnlegen(false)
      // Was gelang, sagt das Kästchen oben rechts (7.2 Abschnitt 34, R1130).
      if (hinweis) {
        meldeErfolg(hinweis)
      }
      return folge
    } catch (fehler) {
      // Nur die fachliche Absage steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
      return []
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Die Buchungen eines Schritts im letzten Lauf (R837) — die eine Zahl, die
   * die zweite Liste beantwortet hat. Gezählt wird über die Vorgänge des
   * Werkzeugs. Ein Werkzeug, das sich einen Vorgang mit einem anderen teilt
   * (der Ausweis der nicht beherrschenden Gesellschafter bucht innerhalb der
   * Kapitalkonsolidierung), zeigt nichts: Die Zahl wäre nicht seine.
   */
  if (bild === null) {
    return (
      <section className="konsolidierungsschritte">
        <h1>{t('bereich.abschluss.schritte')}</h1>
        {bildabruf.ausgefallen && <Abrufausfall abruf={bildabruf} />}
      </section>
    )
  }

  return (
    <section className="konsolidierungsschritte">
      <h1>{t('bereich.abschluss.schritte')}</h1>

      {/*
        **Die Befunde stehen über der Folge** (R783): Wer die Folge pflegt,
        soll im selben Bild sehen, was durchfällt — dort ist die Korrektur
        einen Handgriff entfernt. Dieselbe Warnung erscheint noch einmal in
        der Freigabeprüfung, damit sie auch der sieht, der freigibt.
      */}
      {/*
        **Wessen Folge?** Die der Vorlage ab Werk, und nur sie (R1610). Der
        Satz sagt, wo die Schritte eines bestehenden Abschlusses stehen.
      */}
      <p className="erlaeuterung">
        <Verweissatz
          text={t('schritte.nurvorlage', undefined, true)}
          aufVerweis={() => (window.location.hash = adresseVon({ bereich: 'abschluss', unterpunkt: 'konzernabschluesse' }))}
        />
      </p>

      {/*
        **Hart ist, was den Lauf verhindert** — alles andere ist eine Warnung
        (R783). Bis zum 2026-09-26 standen hier `fehlt` und `abweichend`; beide
        gibt es im Gestaltungsblatt an dieser Stelle nicht, und die Befunde
        standen deshalb alle gleich da. Das Blatt kennt `hart` und `weich`.
      */}
      {/*
        **Jede Meldung führt an die Stelle, wo sie erledigt wird** (7.1
        Abschnitt 13, R1527, R1528): Fehlt ein Prozess, öffnet „Schritt
        hinzufügen" das Anlegen mit ihm gewählt; fehlt ein Konto oder eine
        Abgrenzung, öffnet „Abgrenzen" das Fenster des Prozesses; nennt ein
        Schritt einen Prozess, den es nicht gibt, öffnet „Entfernen" die
        Rückfrage.
      */}
      {bild.befunde.length > 0 && (
        <ul className="befunde">
          {bild.befunde.map((b) => (
            <li
              key={`${b.art}:${b.werkzeug}`}
              className={BLOCKIERT.has(b.art) ? 'hart' : 'weich'}
            >
              {/* Ein Rahmen um den Satz: Die Zeile stellt ihre Teile nebeneinander, und der Verweis gehört in den Fluss. */}
              <span>
                <Verweissatz text={satz(b.meldung, undefined, true)} aufVerweis={verweisDesBefunds(b)} />
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('schritte.spalte.stelle')}</th>
              <th>{t('schritte.spalte.name')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {bild.folge.length === 0 && (
              <tr>
                <td colSpan={3} className="leerhinweis">
                  {t('schritte.leer')}
                </td>
              </tr>
            )}
            <Schrittfolge
              bauart="vorlage"
              folge={bild.folge}
              werkzeuge={bild.werkzeuge}
              laeuft={laeuft}
              aufOeffnen={setFenster}
              aufEntfernen={darf('konsolidierungsschrittEntfernen') ? setLoeschen : undefined}
            />
          </tbody>
        </table>
      </div>

      {darf('konsolidierungsschrittPflegen') && (
        <div className="knopfreihe">
          <button type="button" disabled={laeuft} onClick={() => setAnlegen(true)}>
            {t('schritte.neu')}
          </button>
        </div>
      )}

      {anlegen && darf('konsolidierungsschrittPflegen') && (
        <div className="stammdatenfelder schrittanlage">
          <label>
            <span className="name">{t('schritte.feld.werkzeug')}</span>
            <Auswahlfeld
              wert={entwurf.werkzeug}
              eintraege={werkzeugwahl}
              sorte="aufzaehlung"
              leer={t('schritte.feld.werkzeug.leer')}
              beschriftung={t('schritte.feld.werkzeug')}
              aufWahl={(werkzeug) =>
                setEntwurf((e) => ({
                  werkzeug,
                  // Der Name des Werkzeugs als Vorschlag: Wer nichts anderes
                  // will, tippt nichts — und ein Schritt ohne Namen entsteht
                  // gar nicht erst.
                  name: e.name.trim() === '' ? t(`werkzeug.${werkzeug}` as Sprachschluessel) : e.name,
                }))
              }
            />
            <span className="erlaeuterung">{t('schritte.feld.werkzeug.hinweis')}</span>
          </label>

          <label>
            <span className="name">{t('schritte.feld.name')}</span>
            <input
              value={entwurf.name}
              onChange={(e) => setEntwurf((v) => ({ ...v, name: e.target.value }))}
            />
            <span className="erlaeuterung">{t('schritte.feld.name.hinweis')}</span>
          </label>

          <div className="aktionsreihe">
            <button
              type="button"
              className="haupt"
              disabled={laeuft || entwurf.werkzeug === '' || entwurf.name.trim() === ''}
              onClick={() =>
                void tue(
                  () => api.konsolidierungsschrittPflegen(null, entwurf.werkzeug, entwurf.name),
                  t('erfolg.schritt.angelegt'),
                ).then(() => setEntwurf({ werkzeug: '', name: '' }))
              }
            >
              {t('schritte.anlegen')}
            </button>
            <button type="button" disabled={laeuft} onClick={() => setAnlegen(false)}>
              {t('schritte.abbrechen')}
            </button>
          </div>
        </div>
      )}

      {/*
        **Der Nachweis liegt im Fenster des Prozesses** (R814, R815, R825) —
        dort geklammert nach Fall. Bis zum 2026-09-27 stand er hier ein
        zweites Mal, flach unter der Folge; zwei Darstellungen derselben
        Buchungen sind eine zu viel.
      */}

      {/*
        **Dasselbe Fenster wie unter dem Konzernabschluß** (R825): Was der eine
        Bereich an Handgriffen anbietet, bietet der andere auch.
      */}
      {fenster && (
        <Prozessfenster
          schritt={fenster}
          werkzeug={bild.werkzeuge.find((w) => w.schluessel === fenster.werkzeug)}
          konzernabschluss=""
          parameter={null}
          ausgabe={null}
          kontowahl={kontowahl}
          positionswahl={positionswahl}
          bezug={null}
          aufGespeichert={(neu) => bildabruf.setze({ ...bild, folge: neu })}
          aufSchliessen={() => setFenster(null)}
        />
      )}

      {meldung && <p className="meldung">{meldung}</p>}

      {loeschen && darf('konsolidierungsschrittEntfernen') && (
        <Nachfrage
          titel={t('schritte.entfernen.titel')}
          frage={t('schritte.entfernen.frage', {
            name: beschriftung(loeschen.namen) || loeschen.schluessel,
          })}
          hinweis={t('schritte.entfernen.hinweis')}
          bestaetigen={t('schritte.entfernen')}
          gefahr
          aufJa={() =>
            void tue(() => api.konsolidierungsschrittEntfernen(loeschen.schluessel), t('erfolg.schritt.entfernt'))
          }
          aufSchliessen={() => setLoeschen(null)}
        />
      )}
    </section>
  )
}
